import type { DocumentPreviewHighlight } from "components/Document/shared/DocumentPreview";

import { boxRegion, fieldPathOf } from "components/Document/ReviewInteraction";
import { safeParseInt } from "encamp-shared/src/tierii/utilities";
import { FocusEvent, useCallback, useMemo, useState } from "react";

import { manifestFieldBox } from "../components/manifestFieldBoxes";

/**
 * The document pane follows the field the reviewer is in: it scrolls that
 * field's box on EPA Form 8700-22 into view and outlines it.
 *
 * Transcribing a poor scan means reading a value off the page and typing it into
 * a form, and the two are not laid out the same way — the reviewer loses their
 * place on the document every time they look down. Pointing at the box removes
 * the search half of that loop.
 *
 * The outline is a hint and nothing more. The region comes from the form's fixed
 * layout, not from anything measured on this document, so a rotated or cropped
 * scan lands it imprecisely.
 */

/** Which page of the PDF each part of the manifest was read from. */
export interface ReadingPages {
  /** The page the main 8700-22 is on, 1-based. Null when the model could not tell. */
  manifestPageNumber: number | null;
  /** Page per waste line, keyed by the line's printed number. */
  wasteLinePages: ReadonlyMap<string, number>;
  /** Page per transporter, keyed by the transporter's printed number. */
  transporterPages: ReadonlyMap<string, number>;
}

export const NO_READING_PAGES: ReadingPages = {
  manifestPageNumber: null,
  wasteLinePages: new Map(),
  transporterPages: new Map(),
};

/** Matches a repeated field path, e.g. `wasteLines.2.quantity`. */
const ROW_FIELD = /^(wasteLines|transporters)\.(\d+)\./;

/**
 * The page a field's box is printed on.
 *
 * A continuation sheet may be the next page or several pages later — that is a
 * property of how the document was scanned, not of the form — so a row's own
 * captured page is the only thing that can say. Falling back to the manifest's
 * page keeps the pane on the right document when a row has no page of its own,
 * which is every row extracted before pages were captured.
 */
function pageFor(fieldPath: string, rowNumbers: RowNumbers, pages: ReadingPages): number | null {
  const row = ROW_FIELD.exec(fieldPath);
  if (row == null) return pages.manifestPageNumber;

  const [, group, indexText] = row;
  const index = safeParseInt(indexText);
  if (index == null) return pages.manifestPageNumber;
  const number =
    group === "wasteLines" ? rowNumbers.wasteLines[index] : rowNumbers.transporters[index];
  if (number == null) return pages.manifestPageNumber;

  const rowPage =
    group === "wasteLines" ? pages.wasteLinePages.get(number) : pages.transporterPages.get(number);
  return rowPage ?? pages.manifestPageNumber;
}

/**
 * The printed numbers of the rows currently on the form, in form order.
 *
 * The form sorts rows by their printed number while a reading lists them in the
 * order the model returned them, so a row's position in one is not its position
 * in the other. Pages are captured per printed number for that reason, and this
 * is what turns a focused path's index back into the number it belongs to.
 */
export interface RowNumbers {
  wasteLines: readonly string[];
  transporters: readonly string[];
}

export function useDocumentFollowing({
  pages,
  rowNumbers,
  enabled = true,
}: {
  pages: ReadingPages;
  rowNumbers: RowNumbers;
  /** False leaves the pane alone — nothing to follow on a non-manifest document. */
  enabled?: boolean;
}): {
  highlight: DocumentPreviewHighlight | null;
  onFocusCapture: (event: FocusEvent<HTMLElement>) => void;
} {
  const [focusedPath, setFocusedPath] = useState<string | null>(null);

  const onFocusCapture = useCallback((event: FocusEvent<HTMLElement>) => {
    const path = fieldPathOf(event.target);
    // Deliberately not cleared on blur. Focus leaving a field for a button, or
    // for the document pane itself, would otherwise pull the outline away at the
    // moment the reviewer looked up to read it.
    if (path != null) setFocusedPath(path);
  }, []);

  const highlight = useMemo(() => {
    if (!enabled || focusedPath == null) return null;

    const field = manifestFieldBox(focusedPath);
    if (field == null) return null;

    const region = boxRegion(field.variant, field.box, field.rowIndex);
    if (region == null) return null;

    const page = pageFor(focusedPath, rowNumbers, pages);
    // No page means no reading ever said where this is. Guessing page 1 would
    // scroll the reviewer away from wherever they had got to on their own.
    if (page == null) return null;

    // No label. The outline points at a box on a form the reviewer is reading,
    // and a caption sitting over the line above it covers data they cannot
    // afford to lose — on a dense form there is nowhere it does not. It also
    // names the field they just put the caret in, which is the one thing they
    // already know. The region sheet still labels its outlines, because there
    // every box is drawn at once and the number is how you tell them apart.
    return { page, region };
  }, [enabled, focusedPath, pages, rowNumbers]);

  return { highlight, onFocusCapture };
}
